import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, Button, Select } from '@dreamtree-org/twreact-ui';
import { useAppApi } from '@/lib/api';
import Can from '@rbac/Can';
import DataTable from '@/components/DataTable';
import PageShell from '@/components/layout/PageShell';
import PageHeader from '@/components/layout/PageHeader';

/**
 * "Dynamic Tools" list (PRD §3.4) — a purpose-built page instead of the generic
 * CrudPage, adapted from a reference tools screen but to this project's *actual*
 * columns: no category/lang concept here (every tool is a sandboxed JS function,
 * services/sandbox/runDynamicTool.js), so those columns are replaced with the caps
 * that actually exist — timeout/memory — plus the real run/success rollup this
 * project tracks per tool row (no separate run-history table, TDD §5.9's known gap).
 */
function StatusBadge({ enabled }) {
  return <Badge variant={enabled ? 'success' : 'secondary'} size="sm">{enabled ? 'enabled' : 'disabled'}</Badge>;
}

const COLUMNS = [
  {
    key: 'name',
    label: 'Name',
    render: (t) => (
      <div>
        <div className="font-medium text-primary-600">{t.name}</div>
        <div className="text-secondary-500 text-xs max-w-xs truncate" title={t.description}>{t.description}</div>
      </div>
    ),
  },
  { key: 'enabled', label: 'Status', searchable: false, render: (t) => <StatusBadge enabled={!!t.enabled} /> },
  { key: 'run_count', label: 'Runs', searchable: false, render: (t) => t.run_count || 0 },
  { key: 'success_count', label: 'Success', searchable: false, render: (t) => (t.run_count ? `${t.success_count || 0}/${t.run_count}` : '—') },
  { key: 'last_run_at', label: 'Last run', searchable: false, render: (t) => (t.last_run_at ? new Date(t.last_run_at).toLocaleString() : 'never') },
  { key: 'timeout_ms', label: 'Timeout / Memory', searchable: false, render: (t) => `${t.timeout_ms}ms / ${t.memory_mb}MB` },
];

export default function DynamicToolsPage() {
  const { fetchApi } = useAppApi();
  const nav = useNavigate();
  const [tools, setTools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [apps, setApps] = useState([]);
  const [applicationId, setApplicationId] = useState('');

  const reload = useCallback(() => {
    setLoading(true);
    return fetchApi('crud', { model: 'AppDynamicTool', action: 'list', limit: 100 })
      .then((res) => setTools((Array.isArray(res) ? res : res?.data) || []))
      .catch((ex) => setErr((ex.response && ex.response.data && ex.response.data.error) || ex.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { reload(); }, [reload]);

  useEffect(() => {
    fetchApi('applicationsList')
      .then((res) => setApps((Array.isArray(res) ? res : res?.data) || []))
      .catch(() => setApps([]));
  }, []);

  const filteredTools = applicationId
    ? tools.filter(t => t.application_id == null || t.application_id === Number(applicationId))
    : tools;

  return (
    <PageShell className="space-y-3">
      <PageHeader
        title="Dynamic Tools"
        subtitle={<>{tools.length} tool{tools.length === 1 ? '' : 's'} — sandboxed JS functions agents can call on demand.</>}
        actions={(
          <Can module="tools" action="author">
            <Button size="sm" onClick={() => nav('/app-dynamic-tool/new')}>New tool</Button>
          </Can>
        )}
      />

      <DataTable
        data={filteredTools}
        columns={COLUMNS}
        loading={loading}
        error={err}
        emptyText="No tools yet."
        search={{ placeholder: 'Filter tools…', keys: ['name', 'description'] }}
        onRowClick={(t) => nav(`/app-dynamic-tool/${t.id}`)}
        onReload={reload}
        toolbar={(
          <div className="w-48 sm:w-56">
            <Select options={[{ label: 'All applications', value: '' }, ...apps.map(a => ({ label: a.name, value: String(a.id) }))]} value={applicationId} onChange={setApplicationId} placeholder="All applications" searchable />
          </div>
        )}
      />
    </PageShell>
  );
}
